// import TTCommonsBlackWoff from '../../assets/fonts/TTCommonsBlack.woff';
import { createTheme } from "@mui/material";
import { css } from "styled-components";
declare module "@mui/material/styles" {
  interface Theme {
    drawerWidth:   string;
    topbarHeight?: string;
    titleMargin:   string;
    rowSpacing:    string;
    columnSpacing: string;
    rowGap:        string;
    columnGap:     string;
  }

  interface Palette {
    blue:       Palette['primary'];
    lightGreen: Palette['primary'];
    lightBlue:  Palette['primary'];
    green:      Palette['primary'];
    magenta:    Palette['primary'];
    purple:     Palette['primary'];
    white:      Palette['primary'];
    black:      Palette['primary'];
    hover:      Palette['primary'];
    muted:      Palette['primary'];
    lightGrey:  Palette['primary'];
  }

  // allow configuration using `createTheme`
  interface PaletteOptions {
    blue?:       PaletteOptions['primary'];
    lightGreen?: PaletteOptions['primary'];
    lightBlue?:  PaletteOptions['primary'];
    green:       PaletteOptions['primary'];
    magenta?:    PaletteOptions['primary'];
    purple?:     PaletteOptions['primary'];
    muted?:      PaletteOptions['primary'];
    lightGrey?:  PaletteOptions['primary'];
    white:       Palette['primary'];
    black:       Palette['primary'];
    hover:       PaletteOptions['primary'];
  }

  interface ThemeOptions {
    drawerWidth?:   string;
    topbarHeight?:  string;
    titleMargin?:   string;
    columnSpacing?: string;
    rowSpacing?:    string;
    rowGap?:        string;
    columnGap?:     string;
  }
}

export const COLORS = {
  blue:       '#275a86',
  lightBlue:  '#5A8EBB',
  blueAccent: '#3F77A7',
  green:      '#007672',
  lightGreen: '#00AB84',
  magenta:    '#c6007e',
  purple:     '#833177',
  red:        '#ab2328',
  white:      '#ffffff',
  mediumGrey: '#dcdcdc',
  lightGrey:  '#c3c3c3',
  grey:       '#f4f4f0',
  black:      '#272727',
  orange:     '#ff7500',
  muted:      '#8e8e8e',
}

export const DRAWER_WIDTH = '24rem'
export const TOPBAR_HEIGHT = '6rem'

const baseTheme = createTheme({
  palette: {
    primary: {
      main: COLORS.lightGreen,
      contrastText: COLORS.white
    },
    secondary: {
      main: COLORS.black,
      contrastText: COLORS.white
    },
    blue: {
      main: COLORS.blue,
      contrastText: COLORS.white,
    },
    lightBlue: {
      main: COLORS.lightBlue
    },
    lightGreen: {
      main: COLORS.lightGreen,
      contrastText: COLORS.white
    },
    green: {
      main: COLORS.green,
      contrastText: COLORS.white
    },
    magenta: {
      main: COLORS.magenta,
      contrastText: COLORS.white
    },
    purple: {
      main: COLORS.purple,
      contrastText: COLORS.white
    },
    white: {
      main: COLORS.white,
      contrastText: COLORS.black,
      light: COLORS.white,
      dark: COLORS.black,
    },
    black: {
      main: COLORS.black,
      contrastText: COLORS.white,
      light: COLORS.white,
      dark: COLORS.black,
    },
    background: {
      default: COLORS.grey
    },
    hover: {
      main: COLORS.lightGrey
    },
    success: {
      main: COLORS.lightGreen,
    },
    error: {
      main: COLORS.red
    },
    info: {
      main: COLORS.blue
    },
    muted: {
      main: COLORS.muted
    }
  },
  spacing:       [0, '0.5rem', '1rem', '2rem', '3rem', '4rem', '5rem'],
  topbarHeight:  TOPBAR_HEIGHT,
  drawerWidth:   DRAWER_WIDTH,
  titleMargin:   '4rem',
  rowSpacing:    '4rem',
  columnSpacing: '2rem',
  rowGap:        '4rem',
  columnGap:     '4rem'
})

export const theme = createTheme({
  ...baseTheme,
  typography: {
    fontFamily: '"TT Commons", Arial',
    fontWeightBold: 600,
    fontWeightMedium: 500,
    htmlFontSize: 10,
    fontSize: 16,
    h1: { fontSize: 48, fontWeight: 600 },
    h2: { fontSize: 36, fontWeight: 600 },
    h3: { fontSize: 28, fontWeight: 600 },
    h4: { fontSize: 24, fontWeight: 600 },
    h5: { fontSize: 20, fontWeight: 600, lineHeight: 1.1 },
    h6: { fontSize: 16, fontWeight: 600, lineHeight: 1.1 },
    subtitle1: { fontSize: 24 },
    subtitle2: {fontSize: 16},
    body2: { fontSize: 14 },
    body1: { fontSize: 18 },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        maxWidthXs: {
          [baseTheme.breakpoints.up('xs')]: {
              maxWidth: '30rem'
          },
        },
        maxWidthSm: {
          [baseTheme.breakpoints.up('sm')]: {
              maxWidth: '50rem'
          },
        },
        maxWidthMd: {
          [baseTheme.breakpoints.up('md')]: {
            maxWidth: '70rem'
          },
        },
        maxWidthLg: {
          [baseTheme.breakpoints.up('lg')]: {
            maxWidth: '80rem'
          }
        },
        maxWidthXl: {
          [baseTheme.breakpoints.up('xl')]: {
            maxWidth: '100rem'
          }
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '4rem',
          fontSize: '1.2rem',
          padding: '1.2rem 3rem',
          boxShadow: 'none',
          fontWeight: 600,
          letterSpacing: '.1rem',
          '&:hover': {
            boxShadow: 'none',
          },
          '&:focus': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          width: 'fit-content'
        }
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        }
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 'none',
          '&:before': {
            opacity: 0,
          }
        }
      }
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: 'none'
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          padding: '2rem 2rem',
          borderRadius: '0',
          backgroundColor: baseTheme.palette.background.default,
          minWidth: '40rem',
        }
      }
    },
    MuiModal: {
      styleOverrides: {
        root: {
          borderRadius: '0',
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root:{
          marginLeft: '0'
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: '0.6rem',
          color: 'white !important',
          marginRight: '0.5rem',
        },
        switchBase: {
          '&.Mui-checked+.MuiSwitch-track': {
            backgroundColor: baseTheme.palette.primary.main,
            opacity: 1,
          }
        },
        thumb: {
          boxShadow: 'none',
          color: 'white'
        },
        track: {
          borderRadius: '2rem',
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: baseTheme.typography.fontWeightRegular,
          background: 'rgba(39, 39, 39, 0.1)',
          marginRight: baseTheme.spacing(2),
          padding: '1rem 2rem',
          minWidth: '3rem',
          minHeight: '1rem',
          fontSize: '1.8rem',
          lineHeight: '1',
          transition: 'color 0.2s ease, background 0.2s ease',

          "&:hover":{
            background: 'rgba(39, 39, 39, 0.2)'
          },
          "&.Mui-selected": {
            fontWeight: baseTheme.typography.fontWeightRegular,
            background: COLORS.lightBlue,
            color: COLORS.white
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          display: 'none',
        },
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.white,
          padding:         '1.2rem 1.6rem',
          borderRadius:    '6px'
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          padding: 0,
        },
        root: {
          backgroundColor: baseTheme.palette.background.default,
          borderRadius: 0,
        }
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          padding: '1.2rem 1.6rem',
          borderRadius: '6px',
          "&&.Mui-focused fieldset": {
            borderColor: COLORS.blue,
          }
        },
        input: {
          padding: 0,
        },
        notchedOutline: {
          borderColor: 'transparent',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: '0rem',
          boxShadow: 'none',
          marginTop: baseTheme.spacing(1),
          backgroundColor: COLORS.white,
        },
      }
    },
    MuiFormControlLabel : {
      styleOverrides: {
        root: {
          marginLeft: '0rem'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '.&MuiAccordion-root:before': {
            opacity: 0,
          }
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.grey,
          boxShadow: 'none',
          marginBottom: '2rem',
          color: 'black'
        }
      }
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            fontWeight: 600,
          },
        }
      },
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '1rem 1.5rem',
          minWidth: '12rem',
          '&:not(:last-child)': {
            borderBottom: '1px solid #eee',
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 32,
          color: 'white'
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          '.span': {
            '&.Mui-selected': {
              fontWeight: 600,
            }
          }
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: COLORS.black,
          fontWeight: baseTheme.typography.fontWeightBold,
          fontSize: baseTheme.typography.fontSize,
          textTransform: 'uppercase',
          marginBottom: baseTheme.spacing(1)
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: COLORS.black
        }
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
          fontWeight: 600,
          background: "red",
        },
      },
        gutterBottom: {
          marginBottom: baseTheme.spacing(2),
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paperAnchorLeft: {
          background: baseTheme.palette.blue.main,
          width: DRAWER_WIDTH
        }
      } 
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: baseTheme.palette.white.main,
          "&.Mui-selected": {
            fontWeight: 'inherit',
            backgroundColor: COLORS.blueAccent,

            "&:hover": {
              backgroundColor: 'rgba(0, 0, 0, 0.05)'
            }
          },
          "&:hover": {
            backgroundColor: 'rgba(0, 0, 0, 0.05)'
          }
        },
      },
    },
  },
})

// blue: ,
// green: #007672,
// lightGreen: #00ab84,
// magenta: #c6007e,
// purple: #833177,
// red: #ab2328,
// white: #ffffff,
// mediumGrey: #dcdcdc,
// grey: #f4f4f0,
// black: #272727,
// orange: #ff7500;

export const editorStyles = css`
  .sun-editor-editable {
    h1, h2, h3, h4, h5, h6 {
      margin: 0 0 1.5rem;
      line-height: 1.1;
    }
  
    h1 {
      font-size: 4.8rem;
    }
  
    h2 {
      font-size: 3.6rem;
    }
  
    h3 {
      font-size: 2.8rem;
    }
  
    h4 {
      font-size: 2.4rem;
    }
  
    h5 {
      font-size: 1.6rem;
    }
  
    p {
      font-size: 1.8rem;
    }

    ol,
    ul {
      padding: 0 0 0 1rem;
      list-style-position: inside;
    }

    li {
      font-size: 1.8rem;
      padding-left: 1.5rem;
    }

    ul {
      list-style: none;

      li {
        position: relative;
      }

      li::before {
        content: '·';
        position: absolute;
        top: 0;
        left: 0;
        font-weight: 700;
      }
    }

    ol {
      list-style: none;
      counter-reset: my-awesome-counter;

      li {
        position: relative;
        counter-increment: my-awesome-counter;
        padding-left: 2rem;
      }

      li::before {
        content: counter(my-awesome-counter) ". ";
        position: absolute;
        top: 0;
        left: 0;
        font-weight: 700;
      }
    }
  }
`
